import * as pc from 'playcanvas';
import { LEVEL_CONFIG, ARENA_NAMES, LEVEL_COLORS, MAX_LEVELS } from '../config/levels.js';
import { playSound } from '../systems/audio.js';
import { getFirstGamepad, isButtonPressed } from '../config/controls.js';
import { GAMEPAD_BUTTONS } from '../config/controls.js';
import { isTouchDevice } from '../systems/touch.js';

// Show level intro screen
export function showLevelIntro(level, callback) {
    const config = LEVEL_CONFIG[level];

    const intro = document.createElement('div');
    intro.id = 'level-intro';
    intro.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.9);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        color: white;
        font-family: Arial, sans-serif;
    `;

    intro.innerHTML = `
        <div style="font-size: 24px; opacity: 0.7; margin-bottom: 10px;">LEVEL ${level}</div>
        <div style="font-size: 48px; font-weight: bold; margin-bottom: 20px; color: ${LEVEL_COLORS[level]};">
            ${config.name.toUpperCase()}
        </div>
        <div style="font-size: 18px; opacity: 0.6;">${ARENA_NAMES[config.arena]}</div>
        <div style="font-size: 14px; opacity: 0.4; margin-top: 30px;">GET READY...</div>
    `;

    document.body.appendChild(intro);

    // Fade out and start
    setTimeout(() => {
        intro.style.transition = 'opacity 0.5s';
        intro.style.opacity = '0';
        setTimeout(() => {
            intro.remove();
            callback();
        }, 500);
    }, 2000);
}

// Show victory message
export function showVictoryMessage(currentLevel, onContinue) {
    const isLastLevel = currentLevel >= MAX_LEVELS;

    if (isLastLevel) {
        playSound('victory');
    } else {
        playSound('levelUp');
    }

    const msg = document.createElement('div');
    msg.id = 'victory-message';
    msg.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: linear-gradient(135deg, #FFD700, #FFA500);
        color: white;
        padding: 40px 60px;
        border-radius: 20px;
        font-size: 32px;
        font-weight: bold;
        text-align: center;
        z-index: 10000;
        box-shadow: 0 10px 40px rgba(0,0,0,0.3);
    `;

    const continueText = isTouchDevice() ? 'Tap to continue' : 'Press SPACE or A to continue';
    const playAgainText = isTouchDevice() ? 'Tap to play again' : 'Press SPACE or A to play again';

    if (isLastLevel) {
        msg.innerHTML = `🏆 GAME COMPLETE!<br><span style="font-size: 18px">You defeated all bosses!<br>${playAgainText}</span>`;
    } else {
        const nextBossName = LEVEL_CONFIG[currentLevel + 1].name;
        msg.innerHTML = `🎉 LEVEL ${currentLevel} COMPLETE!<br><span style="font-size: 18px">Next: ${nextBossName}<br>${continueText}</span>`;
    }

    document.body.appendChild(msg);

    // Wait for input to continue
    waitForContinue(msg, () => {
        onContinue(isLastLevel);
    });
}

// Show defeat message with retry and restart options
export function showDefeatMessage(currentLevel, onRetry, onRestart) {
    playSound('defeat');

    const config = LEVEL_CONFIG[currentLevel];

    const msg = document.createElement('div');
    msg.id = 'defeat-message';
    msg.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: linear-gradient(135deg, #e74c3c, #c0392b);
        color: white;
        padding: 40px 60px;
        border-radius: 20px;
        font-size: 32px;
        font-weight: bold;
        text-align: center;
        z-index: 10000;
        box-shadow: 0 10px 40px rgba(0,0,0,0.3);
    `;

    const retryLabel = isTouchDevice() ? '🔄 RETRY' : '🔄 RETRY (SPACE)';
    const menuLabel = isTouchDevice() ? '🏠 MENU' : '🏠 MAIN MENU (ESC)';

    msg.innerHTML = `
        💔 DEFEATED BY ${config.name.toUpperCase()}!
        <div style="margin-top: 25px; display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
            <button id="retry-btn" style="
                padding: 15px 30px;
                font-size: 18px;
                font-weight: bold;
                border: none;
                border-radius: 10px;
                cursor: pointer;
                background: #27ae60;
                color: white;
                transition: transform 0.1s;
                -webkit-tap-highlight-color: transparent;
                touch-action: manipulation;
                min-width: 120px;
            ">${retryLabel}</button>
            <button id="restart-btn" style="
                padding: 15px 30px;
                font-size: 18px;
                font-weight: bold;
                border: none;
                border-radius: 10px;
                cursor: pointer;
                background: #3498db;
                color: white;
                transition: transform 0.1s;
                -webkit-tap-highlight-color: transparent;
                touch-action: manipulation;
                min-width: 120px;
            ">${menuLabel}</button>
        </div>
    `;
    document.body.appendChild(msg);

    // Wait for input with two options
    waitForDefeatInput(msg, onRetry, onRestart);
}

// Wait for defeat screen input (retry or restart)
function waitForDefeatInput(msgElement, onRetry, onRestart) {
    let keyboardHandler = null;
    let checkInterval = null;
    let waitingForRelease = true;

    const cleanup = () => {
        if (keyboardHandler) {
            document.removeEventListener('keydown', keyboardHandler);
        }
        if (checkInterval) {
            clearInterval(checkInterval);
        }
        msgElement.remove();
    };

    // Button click/touch handlers
    const retryBtn = document.getElementById('retry-btn');
    const restartBtn = document.getElementById('restart-btn');

    const handleRetry = (e) => {
        e.preventDefault();
        e.stopPropagation();
        cleanup();
        onRetry();
    };

    const handleRestart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        cleanup();
        if (onRestart) onRestart();
    };

    if (retryBtn) {
        retryBtn.addEventListener('click', handleRetry);
        retryBtn.addEventListener('touchend', handleRetry, { passive: false });
        retryBtn.onmouseenter = () => retryBtn.style.transform = 'scale(1.05)';
        retryBtn.onmouseleave = () => retryBtn.style.transform = 'scale(1)';
    }

    if (restartBtn) {
        restartBtn.addEventListener('click', handleRestart);
        restartBtn.addEventListener('touchend', handleRestart, { passive: false });
        restartBtn.onmouseenter = () => restartBtn.style.transform = 'scale(1.05)';
        restartBtn.onmouseleave = () => restartBtn.style.transform = 'scale(1)';
    }

    // Start checking after a short delay
    setTimeout(() => {
        keyboardHandler = (e) => {
            if (e.code === 'Space' || e.code === 'Enter') {
                cleanup();
                onRetry();
            } else if (e.code === 'Escape' || e.code === 'KeyR') {
                cleanup();
                if (onRestart) onRestart();
            }
        };
        document.addEventListener('keydown', keyboardHandler);

        checkInterval = setInterval(() => {
            const pad = getFirstGamepad();
            if (!pad) return;

            const aHeld = isButtonPressed(pad, GAMEPAD_BUTTONS.A);
            const bHeld = isButtonPressed(pad, GAMEPAD_BUTTONS.B);

            // Wait for button release first
            if (waitingForRelease) {
                if (!aHeld && !bHeld) {
                    waitingForRelease = false;
                }
                return;
            }

            if (aHeld) {
                cleanup();
                onRetry();
            } else if (bHeld) {
                cleanup();
                if (onRestart) onRestart();
            }
        }, 16);
    }, 500);
}

// Wait for continue input (keyboard, gamepad, or touch)
function waitForContinue(msgElement, callback) {
    let waitingForRelease = true;
    let keyboardHandler = null;
    let touchHandler = null;
    let checkInterval = null;

    const cleanup = () => {
        if (keyboardHandler) {
            document.removeEventListener('keydown', keyboardHandler);
        }
        if (touchHandler) {
            msgElement.removeEventListener('touchend', touchHandler);
            msgElement.removeEventListener('click', touchHandler);
        }
        if (checkInterval) {
            clearInterval(checkInterval);
        }
        msgElement.remove();
    };

    // Start checking after a short delay
    setTimeout(() => {
        // Keyboard handler
        keyboardHandler = (e) => {
            if (e.code === 'Space' || e.code === 'Enter') {
                cleanup();
                callback();
            }
        };
        document.addEventListener('keydown', keyboardHandler);

        // Touch/click handler for the modal
        touchHandler = (e) => {
            e.preventDefault();
            cleanup();
            callback();
        };
        msgElement.addEventListener('touchend', touchHandler, { passive: false });
        msgElement.addEventListener('click', touchHandler);
        msgElement.style.cursor = 'pointer';

        // Gamepad handler
        checkInterval = setInterval(() => {
            const pad = getFirstGamepad();
            if (!pad) return;

            const aHeld = isButtonPressed(pad, GAMEPAD_BUTTONS.A);

            // Wait for button release first
            if (waitingForRelease) {
                if (!aHeld) {
                    waitingForRelease = false;
                }
                return;
            }

            if (aHeld) {
                cleanup();
                callback();
            }
        }, 16);
    }, 500);
}

// Remove any active modals
export function clearModals() {
    const modals = ['level-intro', 'victory-message', 'defeat-message'];
    modals.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
    });
}
